/**
 * 広域マスタ 編集モーダル冒頭に出す「適用期間が終了しています」案内 Alert。
 *
 * design 正: 8080 プロト `admin/occto.html` の `_occtoSyncExpiryNotice`
 * (data-occto-expiry-notice)。 一覧では「終了」バッジで状態が分かるが、 編集
 * モーダルを開くと日付欄が 2 つ並ぶだけで「終了している」ことも「延長すれば
 * 戻せる」ことも読めなかった、 というのが元の課題。
 *
 * 判定は「form の適用終了日 (validTo) が過去日か」だけで、 マスタ種別で分岐
 * しない。 そのため適用期間を持つ 7 タブ (事業者 / BG / 計画提出者 / 発電所 /
 * 発電販売計画 / 需要調達計画 / 需要抑制計画 = `TopLevelValidPeriodFields` を
 * 使う全タブ) にそのまま効く。 取引先タブはモーダルトップレベルに適用期間を
 * 持たないため対象外。
 *
 * status は **info**。 適用期間の終了はエラーでは
 * なく、 事業終了をそのまま記録した正しい状態でもある (Des 判断 2026-08-05)。
 *
 * 文言は「状態 (title) → 影響 1 文 (description) → 操作 (button)」の 3 要素
 * だけに絞る。 直し方 (先の日付にする / 空欄 = 無期限) は適用終了日欄の直下
 * (`TopLevelValidPeriodFields` の注意文) が持ち、 帯と欄で同じ説明を繰り返さない。
 */
import { type Control, type FieldValues, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Alert, TertiaryButton } from '@enechain/ecloud-designsystem'

import type { CivilDate } from '~/models/civilDate'
import { topLevelValidToFieldId } from '~/pages/AdminOcctoMaster/tabs/_common/components/TopLevelValidPeriodFields'
import { useTodayJst } from '~/pages/AdminOcctoMaster/tabs/_common/hooks/useTodayJst'
import { isValidToPast } from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type Props = {
  /**
   * form の Control。 個別 form の Input 型は discriminated union を含み
   * `FieldValues` への narrowing が難しいため、 呼び出し側で
   * `Control<FormInput> as unknown as Control<FieldValues>` に cast して渡す
   * (`TopLevelValidPeriodFields` と同じ pattern)。 本部品は `validTo` にしか
   * アクセスしないので型安全性への影響はローカルに閉じる。
   */
  control: Control<FieldValues>
  /**
   * `TopLevelValidPeriodFields` に渡しているものと同じ id prefix。
   * 「適用終了日を変更」ボタンのジャンプ先 id をここから解決する。
   */
  idPrefix: string
}

export const ExpiredMasterAlert = ({
  control,
  idPrefix,
}: Props): React.ReactElement | null => {
  const { t } = useTranslation()
  const today = useTodayJst()
  const validTo = useWatch({ control, name: 'validTo' }) as
    | CivilDate
    | undefined

  // 適用終了日を直した瞬間に帯が消える (= 直ったことがその場で分かる)。
  if (!isValidToPast(validTo, today)) return null

  return (
    <Alert
      description={t('page:adminOcctoMaster.common.expiredNotice.description')}
      status="info"
      // 適用終了日は見出しに入れない (2026-10-20 デザイナー指示: Alert に個別の値を並べない。
      // 同じモーダルの適用終了日の欄に出ている。 UI 辞書 alert-placement.md)。
      // [spec 差異] spec AC-MOI-022 は「（〜{適用終了日}）」 を添えるとする
      title={t('page:adminOcctoMaster.common.expiredNotice.title')}
    >
      {/* DS Alert は children を icon + 本文の隣に flex で並べるため、
          auto margin で右端に寄せる (design `.ecl-alert-body { flex: 1 }` 相当)。 */}
      <TertiaryButton
        flexShrink={0}
        marginInlineStart="auto"
        onClick={() => {
          const target = document.getElementById(
            topLevelValidToFieldId(idPrefix),
          )
          if (target === null) return
          // `CompactCivilDateField` の id は visually-hidden な native
          // `<input type="date">` に付く (= キーボード経路の入口)。 focus すると
          // 親 Box の _focusWithin outline が可視の入力欄側に出るため、 どこへ
          // 飛ばされたかが見て分かる。
          target.scrollIntoView({ block: 'center', behavior: 'smooth' })
          target.focus()
        }}
        size="sm"
      >
        {t('page:adminOcctoMaster.common.expiredNotice.action')}
      </TertiaryButton>
    </Alert>
  )
}
